import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import "../../config";
import "../../assets/css/gallery.scss";
import api from '../../utils/api';

import GalleryCard from "./GalleryCard";
import GalleryList from "./GalleryList";
import GalleryFilter from "./GalleryFilter";
import Details from "../Modals/Details";
import Import from "./Import";
import Pagination from "../UC/Pagination";
import { useGlobalPopup } from "../../contexts/GlobalPopupContext";
import { useTranslation } from "../../contexts/TranslationContext";
import ViewStyle from "../UC/ViewStyle";
import Loader from "../UC/Loader";
import { use } from "react";

import NoRecordFound from "../UC/NoRecordFound";

const Gallery = ({ selectionMode, onClose, onSelect }) => {
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const editDialogRef = useRef(null);
  const abortControllerRef = useRef(null); // Add ref for AbortController
  const { showDeleteConfirm, showError, showSuccess, showWarning, showConfirm } = useGlobalPopup();
  const { language } = useTranslation();

  // Check if current user has permission to edit/delete a record
  const checkUserPermission = (record) => {
    try {
      const userData = localStorage.getItem("FWUser");
      if (!userData) {
        return false;
      }
      const parsedUser = JSON.parse(userData);
      const currentUserId = parsedUser.Information?.User_ID || parsedUser.User_ID;

      return currentUserId.toString() === record.User_ID.toString(); // Ensure both are strings for comparison
    } catch (error) {
      console.error("Error checking user permission:", error);
      return false;
    }
  };

  const defaultFilterCatalog = {
    Make_IDs: [],
    ShoeTypes: [],
    Models: [],
    ProductCode: "",
  };

  const loginAccount = useMemo(() => {
    const stored = localStorage.getItem("FWUser");
    return stored ? JSON.parse(stored) : null;
  }, []);

  const defaultFilterEvidence = {
    Scanner_IDs: [loginAccount && loginAccount.Information ? `${loginAccount.Information.User_ID}-${loginAccount.Information.User_ID_Ext}` : null].filter(id => id), // Set to current user scanner if available
    Category_IDs: [], // Default to both types for evidence
    CaseIncidents: [],
  };

  const [selectedRecord, setSelectedRecord] = useState();
  const [editRecord, setEditRecord] = useState(null);
  const [selectedId, setSelectedId] = useState('0'); // Track selected card ID
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showGalleryType] = useState(!selectionMode); // setShowGalleryType removed - not currently used
  const [galleryType, setGalleryType] = useState(selectionMode ? "evidence" : "catalog");
  const [filters, setFilters] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [resultSummary, setResultSummary] = useState(0);
  const [arrRelatedAccounts, setArrRelatedAccounts] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isSearching, setIsSearching] = useState(false); // Add loading state
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false); // Mobile filter panel state
  const [isMobileFilterOpening, setIsMobileFilterOpening] = useState(false); // Track when mobile filter is opening
  const [isSelectionFilterOpen, setIsSelectionFilterOpen] = useState(false); // Selection mode filter panel state
  const [isSelectionFilterOpening, setIsSelectionFilterOpening] = useState(false); // Track when selection filter is opening
  const [isFilterReady, setIsFilterReady] = useState(false); // Track when GalleryFilter is ready

  const handleEditGallery = (record) => {
    // Check if current user has permission to edit this record
    if (!checkUserPermission(record)) {
      showWarning(
        language.gallery?.noPermission || 'No Permission',
        language.gallery?.noEditPermission || 'You do not have permission to edit this item. You can only edit items you created.'
      );
      return;
    }

    console.log("Editing gallery:", record);
    setEditRecord(record);
    if (editDialogRef.current) {
      editDialogRef.current.showModal();
    }
  };

  const handleCloseEditModal = () => {
    if (editDialogRef.current) {
      editDialogRef.current.close();
    }
    setEditRecord(null);
    // Note: Removed automatic refresh - only refresh if edit was successful
    // searchGallery will be called from Import component's onClose callback if needed
  };

  const handleEditSuccess = () => {
    // Only refresh gallery data when edit is actually successful
    console.log("Edit successful, refreshing gallery data");
    searchGallery(pageIndex, pageSize, filters);
  };

  const handleDeleteGallery = (record) => {
    // Check if current user has permission to delete this record
    if (!checkUserPermission(record)) {
      showWarning(
        language.gallery?.noPermission || 'No Permission',
        language.gallery?.noDeletePermission || 'You do not have permission to delete this item. You can only delete items you created.'
      );
      return;
    }

    let itemName = `${record.ImpressionInfo?.CaseIncident} #${record.ImpressionInfo?.ImpressionNumber}` || language.gallery?.thisItem || 'this item';

    if (!record.ImpressionInfo?.CaseIncident && !record.ImpressionInfo?.ImpressionNumber)
      itemName = `this item`;

    showDeleteConfirm(
      language.gallery?.deleteGalleryItem || 'Delete Gallery Item',
      `${language.gallery?.deleteConfirmMessage || 'Are you sure you want to delete'} "${itemName}"? ${language.gallery?.deleteActionWarning || 'This action cannot be undone.'}`,
      async () => {
        try {
          // TODO: Replace with actual API call
          console.log("Deleting gallery:", record);

          // Simulate API call
          const response = await api.post(`${global.config.api.domain}/fwiq-api/api/${galleryType}/delete`,
            [record.IDValue] // Send IDValue as an array
          );

          if (response.status === 200 || response.status === 204) {
            showSuccess(language.gallery?.success || 'Success', `"${itemName}" ${language.gallery?.deleteSuccessMessage || 'has been deleted successfully.'}`);
            // Refresh the gallery data after delete
            searchGallery(pageIndex, pageSize, filters);
          } else {
            showError(language.gallery?.deleteFailed || 'Delete Failed', language.gallery?.deleteFailedMessage || 'Failed to delete the item. Please try again.');
          }
        } catch (error) {
          console.error("Error deleting gallery item:", error);
          showError(language.gallery?.deleteFailed || 'Delete Failed', language.gallery?.deleteErrorMessage || 'An error occurred while deleting the item. Please try again.');
        }
      }
    );
  };

  const handleReUpload = (record) => {
    const itemName = record.Make && record.Model
      ? `${record.Make} ${record.Model}`
      : record.ImpressionInfo?.CaseIncident || language.gallery?.thisItem || 'this item';

    showConfirm(
      language.gallery?.reUploadConfirmTitle || 'Re-Upload Confirmation',
      language.gallery?.reUploadConfirmMessage || `Are you sure you want to re-upload "${itemName}"? This will process the images again.`,
      async () => {
        try {
          console.log("Re-uploading gallery item:", record);

          const response = await api.post(`${global.config.api.domain}/fwiq-api/api/${galleryType}/re-upload`,
            JSON.stringify({
              ids: [record.IDValue]
            }),
            {
              headers: {
                'Content-Type': 'application/json'
              }
            }
          );

          if (response.status === 200 || response.status === 204) {
            showSuccess(
              language.gallery?.reUploadSuccess || 'Success',
              language.gallery?.reUploadSuccessMessage || `"${itemName}" has been re-uploaded successfully.`
            );
            // Refresh the gallery data after re-upload
            searchGallery(pageIndex, pageSize, filters);
          } else {
            showError(
              language.gallery?.reUploadError || 'Re-Upload Failed',
              language.gallery?.reUploadErrorMessage || 'Failed to re-upload the item. Please try again.'
            );
          }
        } catch (error) {
          console.error("Error re-uploading gallery item:", error);
          showError(
            language.gallery?.reUploadError || 'Re-Upload Failed',
            language.gallery?.reUploadErrorMessage || 'An error occurred while re-uploading the item. Please try again.'
          );
        }
      }
    );
  };

  const searchGallery = useCallback((appliedPageIndex = pageIndex, appliedPageSize = pageSize, appliedFilters = filters) => {
    console.log("Searching gallery with filters:", appliedFilters);

    // Abort any ongoing request
    if (abortControllerRef.current) {
      console.log("Aborting previous search request");
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    setIsSearching(true);

    const fetchGalleryData = async () => {
      try {
        // Check if request was aborted before starting
        if (currentController.signal.aborted) {
          console.log("Request aborted before fetch");
          return;
        }

        // // pass IP address of client for audit logging
        // const clientIp = await fetch('https://api64.ipify.org?format=json', {
        //   signal: currentController.signal
        // })
        //   .then((res) => res.json())
        //   .then((data) => data.ip)
        //   .catch((err) => {
        //     if (err.name === 'AbortError') {
        //       console.log("IP fetch aborted");
        //       return null;
        //     }
        //     console.error("Failed to fetch client IP:", err);
        //     return null;
        //   });

        // // Check if request was aborted after IP fetch
        // if (currentController.signal.aborted) {
        //   console.log("Request aborted after IP fetch");
        //   return;
        // }

        // if (clientIp) {
        //   appliedFilters.ClientIp = clientIp;
        // }

        const response = await api.post(`${global.config.api.domain}/fwiq-api/api/${galleryType}/search`, {
          PageNumber: appliedPageIndex,
          PageSize: appliedPageSize,
          ...appliedFilters,
        }, {
          signal: currentController.signal // Add abort signal for axios
        });

        // Check if request was aborted after fetch
        if (currentController.signal.aborted) {
          console.log("Request aborted after fetch");
          return;
        }

        if (response.status === 200) {
          const data = response.data;

          // Final check before updating state
          if (currentController.signal.aborted) {
            console.log("Request aborted before updating state");
            return;
          }

          if (data?.Records) {
            setGalleries(data.Records);
            setResultSummary(data.TotalRecords || 0);
            setArrRelatedAccounts(data.RelatedAccounts || []);
            console.log(`Search completed successfully: ${data.Records.length} records found`);
          } else {
            console.error("Unexpected API response format:", data);
            setGalleries([]);
          }


        } else if (response.status === 204) {
          // 204 No Content - No records found

          setGalleries([]);
          setResultSummary(0);
          setArrRelatedAccounts([]);


        } else {
          console.error("Failed to fetch gallery data");
          setGalleries([]);
        }

      } catch (error) {
        if (error.name === 'AbortError') {
          console.log("Search request was aborted");
          return;
        }
        console.error("Error fetching gallery data:", error);
        setGalleries([]);

      } finally {
        // Only update loading state if this is still the current request
        if (!currentController.signal.aborted) {
          setIsSearching(false);
        }

        abortControllerRef.current = null;
      }
    };

    fetchGalleryData();

  }, [galleryType, filters, pageIndex, pageSize]);

  // reset to index 1 when filters change
  useEffect(() => {
    setPageIndex(1);
  }, [filters]);

  useEffect(() => {
    if (!selectedRecord) {
      return;
    }

    const dialog = dialogRef.current;
    // Guard: showModal() throws if dialog is already open (e.g. switching records without closing)
    if (!dialog.open) {
      dialog.showModal();
    }

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        dialog.close();
        setSelectedRecord(null);
      }
    };

    const handleBackdropClick = (e) => {
      if (e.target === dialog) {
        dialog.close();
        setSelectedRecord(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    dialog.addEventListener("click", handleBackdropClick);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      dialog.removeEventListener("click", handleBackdropClick);
    };
  }, [selectedRecord]);

  useEffect(() => {

    if (!isFilterReady) {

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setIsSearching(true);
      return;
    }

    // Abort any ongoing search when gallery type changes
    if (abortControllerRef.current) {
      console.log("Aborting search due to gallery type change");
      abortControllerRef.current.abort();
    }

    // Reset page index, but don't trigger a search here
    //setPageIndex(1);

  }, [galleryType, isFilterReady]);

  useEffect(() => {
    if (!galleryType) return;

    // Update UI for gallery type
    document.querySelectorAll(".gallery-type").forEach((element) => {
      element.classList.remove("active");
    });

    const activeElement = document.querySelector(`.gallery-type[gltype="${galleryType}"]`);
    if (activeElement) {
      activeElement.classList.add("active");
    }

    // // Try to restore saved filters first, then fall back to defaults
    // const savedFiltersKey = selectionMode ? `galleryFilters_${galleryType}_selection` : `galleryFilters_${galleryType}`;
    // const savedFilters = localStorage.getItem(savedFiltersKey);

    // if (savedFilters) {
    //   try {
    //     const parsedFilters = JSON.parse(savedFilters);
    //     setFilters(parsedFilters);
    //   } catch (error) {
    //     console.error("Error parsing saved filters:", error);
    //     // Fall back to default filters if parsing fails
    //     setDefaultFilters(galleryType);
    //   }
    // } else {
    //   // Set default filters if no saved filters exist
    //   setDefaultFilters(galleryType);
    // }

    // Reset galleries while loading
    setGalleries([]);

    // Reset filter ready state when gallery type changes
    setIsFilterReady(false);

    setDefaultFilters(galleryType);

  }, [galleryType, selectionMode]);

  // Helper function to set default filters
  const setDefaultFilters = (galleryType) => {
    if (galleryType === "evidence") {
      // let loginAccount = JSON.parse(localStorage.getItem("FWUser"));
      // let currentUserID = loginAccount && loginAccount.Information ? `${loginAccount.Information.User_ID}-${loginAccount.Information.User_ID_Ext}` : null;

      setFilters(defaultFilterEvidence);
    }
    else {
      setFilters(defaultFilterCatalog);
    }
  };

  // // Save filters to localStorage whenever they change (but not during initial load)
  // useEffect(() => {
  //   if (!galleryType || galleries.length === 0) return; // Don't save during initial load

  //   const savedFiltersKey = selectionMode ? `galleryFilters_${galleryType}_selection` : `galleryFilters_${galleryType}`;
  //   localStorage.setItem(savedFiltersKey, JSON.stringify(filters));
  // }, [filters, galleryType, selectionMode, galleries.length]);

  // Separate useEffect to handle searchGallery when filters are ready.
  // Debounce (300ms) prevents double-fire when filters change AND pageIndex
  // resets to 1 in the same tick — only the final state triggers a fetch.
  useEffect(() => {
    if (!galleryType || !isFilterReady || isSelectionFilterOpening || isMobileFilterOpening) {
      if (!isFilterReady) setIsSearching(true);
      return;
    }

    const debounceTimer = setTimeout(() => {
      searchGallery(pageIndex, pageSize, filters);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [galleryType, filters, pageIndex, pageSize, searchGallery, isFilterReady, isSelectionFilterOpening, isMobileFilterOpening]);

  // Cleanup function to abort requests on component unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        console.log("Aborting search on component unmount");
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleApplyFilters = (newFilters) => {
    // console.log("Applying new filters:", newFilters);

    setFilters(newFilters);
    setPageIndex(1);

    // Note: Filters are automatically saved by the useEffect hook when they change
  };

  const handlePageChange = (newPageIndex, newPageSize = pageSize) => {
    setPageIndex(newPageIndex);
    setPageSize(newPageSize);
  };

  // Callback for when GalleryFilter is ready
  const handleFilterReady = useCallback((isReady) => {
    console.log("Filter ready:", isReady);
    setIsFilterReady(isReady);
  }, []);

  const toggleMobileFilter = () => {
    if (!isMobileFilterOpen) {
      // Prevent automatic searches when opening mobile filter
      setIsMobileFilterOpening(true);
      setIsMobileFilterOpen(true);
    } else {
      setIsMobileFilterOpen(false);
    }
  };

  const closeMobileFilter = () => {
    setIsMobileFilterOpen(false);
    setIsMobileFilterOpening(false);
  };

  const toggleSelectionFilter = () => {

    if (!isSelectionFilterOpen) {
      // Prevent automatic searches when opening selection filter
      setIsSelectionFilterOpening(true);
      setIsSelectionFilterOpen(true);

    } else {
      setIsSelectionFilterOpen(false);
    }
  };

  const closeSelectionFilter = () => {
    setIsSelectionFilterOpen(false);
    setIsSelectionFilterOpening(false);
  };

  const handleFocusRecord = (record) => {
    // Set the selected ID to highlight the card
    setSelectedId(record.IDValue);
    console.log("focused record:", record.IDValue);

    if (selectionMode) {
      onSelect(record);
    }
  };

  const handleOpenDetails = (record) => {
    if (selectionMode && onSelect) {
      // In selection mode, double-click selects the record
      setSelectedId(record.IDValue);
      console.log("selected record:", record.IDValue);
      onSelect(record);
    } else {
      // In normal mode, double-click opens details
      setSelectedRecord(record);
    }
  };

  // Handle analyze functionality - navigate to analyze page with selected record
  const handleAnalyze = (record) => {
    // Store the selected record in sessionStorage so it can be retrieved by the Analyze component
    sessionStorage.setItem('selectedAnalyzeRecord', JSON.stringify(record));
    // Navigate to analyze page
    navigate('/analyze');
  };

  return (
    <>
      {/* Desktop Filter - Show for both gallery types but hide in selection mode */}
      <div className={`desktop-filter ${selectionMode ? 'hidden-in-selection' : ''}`}>
        <GalleryFilter
          galleryType={galleryType}
          setGalleryType={setGalleryType}
          applyFilters={handleApplyFilters}
          selectionMode={selectionMode}
          onFilterReady={handleFilterReady}
        />
      </div>

      {/* Mobile Filter Toggle Button - Hide in selection mode */}
      <div className={`mobile-filter-toggle ${selectionMode ? 'hidden-in-selection' : ''}`}>
        <button className="filter-toggle-btn" onClick={toggleMobileFilter}>
          <i className="fa fa-filter"></i>
        </button>
      </div>

      {/* Selection Mode Filter Toggle Button - Show only in selection mode */}
      {selectionMode && (
        <div className="selection-mode-filter-toggle-container">
          <button className="selection-mode-filter-toggle" onClick={toggleSelectionFilter}>
            <i className="fa fa-filter"></i>
          </button>
        </div>
      )}

      {/* Mobile Filter Overlay */}
      {isMobileFilterOpen && (
        <div className="mobile-filter-overlay" onClick={closeMobileFilter}>
          <div className="mobile-filter-panel" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-filter-header">
              <h3>{language.gallery?.filters || 'Filters'}</h3>
              <button className="close-filter-btn" onClick={closeMobileFilter}>
                <i className="fa fa-times"></i>
              </button>
            </div>
            <div className="mobile-filter-content">
              <GalleryFilter
                galleryType={galleryType}
                setGalleryType={setGalleryType}
                applyFilters={(filters) => {
                  handleApplyFilters(filters);
                  closeMobileFilter();
                }}
                selectionMode={selectionMode}
                onFilterReady={handleFilterReady}
              />
            </div>
          </div>
        </div>
      )}

      {/* Selection Mode Filter Overlay */}
      {selectionMode && isSelectionFilterOpen && (
        <div className="selection-mode-filter-overlay" onClick={closeSelectionFilter}>
          <div className="selection-mode-filter-panel" onClick={(e) => e.stopPropagation()}>
            <div className="selection-mode-filter-header">
              <h3>{language.gallery?.filters || 'Filters'}</h3>
              <button className="close-filter-btn" onClick={closeSelectionFilter}>
                <i className="fa fa-times"></i>
              </button>
            </div>
            <div className="selection-mode-filter-content">
              <GalleryFilter
                galleryType={galleryType}
                setGalleryType={setGalleryType}
                applyFilters={(filters) => {
                  setFilters(filters);
                  closeSelectionFilter();
                }}
                selectionMode={selectionMode}
                className="floating-panel"
                onFilterReady={handleFilterReady}
              />
            </div>
          </div>
        </div>
      )}

      <div className="tableResult">
        {showGalleryType && (
          <div className="results-type">
            <div
              className={`gallery-type ${galleryType === "catalog" ? "active" : ""}`}
              gltype="catalog"
              onClick={() => {
                if (galleryType !== "catalog") {
                  console.log("Changing gallery type to catalog");
                  setGalleryType("catalog");
                }
              }}
            >
              <label htmlFor="">{language.gallery?.retailGallery || 'RETAIL GALLERY'}</label>
            </div>
            <div
              className={`gallery-type ${galleryType === "evidence" ? "active" : ""}`}
              gltype="evidence"
              onClick={() => {
                if (galleryType !== "evidence") {
                  console.log("Changing gallery type to evidence");
                  setGalleryType("evidence");
                }
              }}
            >
              <label htmlFor="">{language.gallery?.impressionEvidence || 'IMPRESSION EVIDENCE'}</label>
            </div>

            {/* ViewStyle - Hidden on mobile */}
            <div className="desktop-view-style">
              <ViewStyle viewMode={viewMode} setViewMode={setViewMode} />
            </div>
          </div>
        )}

        <div className={`result-view ${viewMode}-view`}>
          {isSearching ? (
            <Loader text={language.gallery?.loadingGalleryData || "Loading gallery data..."} />
          ) : galleries.length === 0 ? (
            <NoRecordFound language={language} />
          ) : viewMode === 'list' ? (
            <GalleryList
              galleries={galleries}
              pageIndex={pageIndex}
              pageSize={pageSize}
              selectedId={selectedId}
              galleryType={galleryType}
              selectionMode={selectionMode}
              handleFocusRecord={handleFocusRecord}
              handleOpenDetails={handleOpenDetails}
              handleEditGallery={handleEditGallery}
              handleDeleteGallery={handleDeleteGallery}
              handleReUpload={handleReUpload}
            />
          ) : (
            galleries.map((record, idx) => (
              <GalleryCard
                key={idx}
                index={(pageIndex - 1) * pageSize + idx + 1}
                record={record}
                isSelected={record.IDValue === selectedId}
                selectRecord={() => handleFocusRecord(record)}
                viewDetails={() => handleOpenDetails(record)}
                hideActions={selectionMode}
                analyzeFunc={handleAnalyze}
                editFunc={(e) => {
                  e.stopPropagation();
                  handleEditGallery(record);
                }}
                deleteFunc={(e) => {
                  e.stopPropagation();
                  handleDeleteGallery(record);
                }}
              />
            ))
          )}
        </div>
        <div className="result-summary">
          <Pagination
            totalRecords={resultSummary || 0}
            pageIndex={pageIndex}
            pageSize={pageSize}
            galleryType={galleryType}
            changePageIndex={(newPageIndex) => {
              
              setPageIndex(newPageIndex);
            }}
            changePageSize={(newPageSize) => {
              setPageSize(newPageSize);
              setPageIndex(1); // Reset to first page when page size changes
            }}
          />
        </div>
      </div>

      <dialog ref={dialogRef} className="dialog">
        {selectedRecord && (
          <Details
            arrAccounts={arrRelatedAccounts}
            record={selectedRecord}
            onClose={() => {
              if (dialogRef.current) {
                dialogRef.current.close();
              }
              setSelectedRecord(null);
            }}
          />
        )}
      </dialog>

      <dialog ref={editDialogRef} className="dialog edit-dialog">
        {editRecord && (
          <Import
            isModal={true}
            editId={editRecord.IDValue}
            onClose={handleCloseEditModal}
            onEditSuccess={handleEditSuccess}
            arrAccounts={arrRelatedAccounts}
          />
        )}
      </dialog>
    </>
  );
};

export default Gallery;
